<script lang="ts" setup>
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import { computed, ref, watch } from "vue";
import BlockUI from "@/components/BlockUI.vue";
import FormAlert from "@/components/FormAlert.vue";
import ChangePasswordModal from "@/components/ChangePasswordModal.vue";
import { useUserProfile, useUserMemberships, useUpdateUserProfile } from "@/queries/users";
import { useActivities } from "@/queries/activities";
import { useSessionStore } from "@/stores/session-store";
import { useAlertStore } from "@/stores/alerts-store";
import ActivityFeed from "@/views/shared/components/ActivityFeed.vue";
import { User } from "@/types";

const alertStore = useAlertStore();
const sessionStore = useSessionStore();
const { profile: profileData, profileLoading, profileError, refetchProfile } = useUserProfile();

// Personal timeline — activities the current user triggered, across all their orgs the
// server can see for this org context. Server scopes to the active OrgId by default.
const currentUserId = computed(() => sessionStore.session?.user?.id ?? "");
const { activities: myActivities, activitiesLoading: myActivitiesLoading } = useActivities({
  actorUserId: currentUserId,
  take: 20,
});
const { memberships, membershipsLoading } = useUserMemberships();
const { updateUserProfile, updateUserProfilePending: saving, updateUserProfileError: saveError, updateUserProfileReset } = useUpdateUserProfile();

const loading = computed(() => profileLoading.value || membershipsLoading.value);

// Only password (CIAM) users can change their password — SSO users' credentials live in their IdP.
const canChangePassword = computed(() => sessionStore.canChangePassword);
const showChangePasswordModal = ref(false);
// Local editable copy — form v-model binds here, not directly to the TanStack cache (profileData is read-only).
// new User(p) rather than a plain spread because the constructor computes initials from the name.
// { immediate: true } is required: on a return visit TanStack may already have cached data when this
// watcher is created, so a plain watch would never fire and the form would be left blank.
const localProfile = ref(new User());

watch(profileData, (p) => {
  if (p) localProfile.value = new User(p);
}, { immediate: true });

const handleSubmit = (event: Event) => {
  event.preventDefault();
  updateUserProfile(localProfile.value, {
    onSuccess: () => {
      alertStore.addSuccess("Profile Updated", "Your profile has been updated successfully.");
    },
  });
};
</script>

<template>
  <PanelGrid :columns="1">
    <BlockUI :active="saving">
      <PanelCard id="userprofile-card">
        <PageHeader
          title="User Profile"
          :breadcrumbs="[
            { label: 'Dashboard', href: '/' },
            { label: 'Organisation', href: '/org' },
            { label: 'Profile' }
          ]"
        />

        <!-- Skeleton while loading or on load error -->
        <SkeletonLoader v-if="loading || profileError" type="form" :fields="6" :error="profileError" @retry="refetchProfile" />

        <template v-else>
          <FormAlert :error="saveError" @dismiss="updateUserProfileReset" class="mb-3" />
          <form @submit="handleSubmit">
            <div class="row mb-3">
              <label for="name" class="col-sm-3 col-form-label">First Name</label>
              <div class="col-sm-9">
                <input
                  type="text"
                  v-model="localProfile.firstName"
                  class="form-control"
                />
              </div>
            </div>
            <div class="row mb-3">
              <label for="name" class="col-sm-3 col-form-label">Last Name</label>
              <div class="col-sm-9">
                <input
                  type="text"
                  v-model="localProfile.lastName"
                  class="form-control"
                />
              </div>
            </div>
            <!--      emailaddress readonly-->
            <div class="row mb-3">
              <label for="email" class="col-sm-3 col-form-label"
                >Email Address</label
              >
              <div class="col-sm-9">
                <input
                  type="email"
                  v-model="localProfile.email"
                  class="form-control-plaintext"
                  readonly
                />
              </div>
            </div>
            <!--      default org-->
            <div class="row mb-3">
              <label for="defaultOrgId" class="col-sm-3 col-form-label"
                >Default Organisation</label
              >
              <div class="col-sm-9">
                <select v-model="localProfile.settings.defaultOrgId" class="form-select">
                  <option
                    v-for="org in memberships"
                    :key="org.orgId"
                    :value="org.orgId"
                  >
                    {{ org.orgName }}
                  </option>
                </select>
              </div>
            </div>
            <div class="row mb-3">
              <label for="defaultTimezoneId" class="col-sm-3 col-form-label"
                >Default Timezone</label
              >
              <div class="col-sm-9">
                <select
                  v-model="localProfile.settings.defaultTimezoneInfo"
                  class="form-select"
                >
                  <option value="">Select Timezone</option>
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">America/New_York</option>
                  <option value="Europe/London">Europe/London</option>
                  <!-- Add more timezones as needed -->
                </select>
              </div>
            </div>
            <div class="row mb-3">
              <label for="theme" class="col-sm-3 col-form-label"
                >Default Theme</label
              >
              <div class="col-sm-9">
                <select v-model="localProfile.settings.theme" class="form-select">
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                </select>
              </div>
            </div>
            <div class="row">
              <div class="col-sm-9 offset-sm-3">
                <div class="d-flex justify-content-end gap-2">
                  <button type="button" class="bb-btn-cancel" @click="$router.back()">Cancel</button>
                  <button type="submit" class="bb-btn-primary-cta">Save Changes</button>
                </div>
              </div>
            </div>
          </form>
        </template>
      </PanelCard>
    </BlockUI>

    <!-- Security — change password (password/CIAM users only) -->
    <PanelCard v-if="canChangePassword" id="userprofile-security-card">
      <h2 class="card-title mb-3">Security</h2>
      <div class="row align-items-center">
        <label class="col-sm-3 col-form-label">Password</label>
        <div class="col-sm-9 d-flex justify-content-between align-items-center">
          <span class="text-secondary">••••••••</span>
          <button type="button" class="bb-btn-primary-cta" @click="showChangePasswordModal = true">
            Change Password
          </button>
        </div>
      </div>
    </PanelCard>

    <ChangePasswordModal v-model:show="showChangePasswordModal" />

    <!-- Personal timeline — what you've done. Server filters by your actor.userId. -->
    <PanelCard id="userprofile-activity-card">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h2 class="card-title mb-0">My Recent Activity</h2>
        <small v-if="myActivitiesLoading" class="text-muted">Loading…</small>
      </div>
      <ActivityFeed
        :activities="myActivities"
        :loading="myActivitiesLoading"
        empty-text="No recent activity. Workflow publishes, project changes, and other actions you take will appear here."
      />
    </PanelCard>
  </PanelGrid>
</template>
